import { invalidateRecordCachesExceptSubject } from '@Data/hooks/recordCacheInvalidation';
import { assertResourceMethodAvailable } from '@Pages/pageRuntime/sdk/endpointGating';
import type { SdkResource, ResourceUrls } from '@Pages/pageRuntime/sdk/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios, { type AxiosError } from 'axios';

type PreviewData = Diagonal.Data.Data.RecordRestorePreviewData;

export interface RestoreResourceRecordState {
  preview: PreviewData | undefined;
  isPreviewLoading: boolean;
  isPreviewError: boolean;
  confirm: () => void;
  isProcessing: boolean;
  /**
   * Transport error from the restore request, surfaced so the dialog can show
   * a 403 (no policy) or 422 (validation) message. `null` on success.
   */
  restoreError: AxiosError | null;
  reset: () => void;
}

export type RestoreResourceRecordResource = Pick<
  SdkResource,
  'id' | 'name' | 'disabled_data_api_endpoints'
> & {
  urls: Pick<ResourceUrls, 'restore'>;
};

interface UseRestoreResourceRecordOptions {
  resource: RestoreResourceRecordResource;
  recordId: string | undefined;
  /**
   * Whether to fetch the restore preview now. Open this gate when the
   * confirmation dialog is opened so the preview matches the moment of decision.
   */
  fetchPreview?: boolean;
  onSuccess?: () => void;
}

export function useRestoreResourceRecord({
  resource,
  recordId,
  fetchPreview = false,
  onSuccess
}: UseRestoreResourceRecordOptions): RestoreResourceRecordState {
  const queryClient = useQueryClient();

  const previewUrl = recordId
    ? `/api/data/resources/${encodeURIComponent(resource.id)}/records/${encodeURIComponent(recordId)}/restore-preview`
    : '';

  const previewQuery = useQuery<PreviewData>({
    enabled: fetchPreview && Boolean(recordId),
    queryKey: ['record-restore-preview', resource.id, recordId, previewUrl],
    queryFn: () => axios.get(previewUrl).then((response) => response.data)
  });

  const restoreMutation = useMutation<{ data?: Record<string, unknown> }, AxiosError>({
    mutationFn: () => {
      assertResourceMethodAvailable(resource, 'restore');

      return axios
        .post(resource.urls.restore(recordId as string))
        .then((response) => response.data);
    },
    onSuccess: (response) => {
      const subjectRecord = response?.data ?? null;
      void invalidateRecordCachesExceptSubject(queryClient, resource.id, recordId, {
        subjectRecord
      });
      onSuccess?.();
    }
  });

  return {
    preview: previewQuery.data,
    isPreviewLoading: previewQuery.isLoading,
    isPreviewError: previewQuery.isError,
    confirm: () => {
      if (!recordId) {
        return;
      }
      restoreMutation.mutate();
    },
    isProcessing: restoreMutation.isPending,
    restoreError: restoreMutation.error,
    reset: () => {
      restoreMutation.reset();
    }
  };
}
